<template>
  <ContentSection>
    <!-- TITLE -->
    <Container class="justify-between gap-10 lg:flex">
      <h2
        v-if="data.heading"
        class="default-component-title"
        :class="data.link ? 'mb-3 xl:mb-8' : 'mb-8 sm:mb-10'"
        v-html="$replaceHtmlTags(data.heading)"
      />
      <AppLink
        class="mb-7 sm:text-md xl:mb-0"
        :to="data.link || `/${route.path.split('/')[1]}`"
        :target="data.target?.value"
      >
        {{ data.link_text || $t('show_all') }}
      </AppLink>
    </Container>

    <!-- ARTICLES -->
    <AnimatedContainer>
      <ul class="hidden grid-cols-4 gap-6 px-12 xl:grid">
        <li
          v-for="article in composedArticles"
          :key="article.id"
          :class="article.colSpan"
          class="slide-in-left"
        >
          <SustainabilityCard :data="article" />
        </li>
      </ul>
    </AnimatedContainer>

    <!-- ARTICLES MOBILE -->
    <div class="flex xl:hidden">
      <Swiper
        :slides-per-view="1.5"
        :space-between="12"
        :slides-offset-before="24"
        :slides-offset-after="24"
        :breakpoints="breakpoints"
        class="max-w-full"
      >
        <SwiperSlide v-for="article in composedArticles" :key="article.id">
          <SustainabilityCard :data="article" />
        </SwiperSlide>
      </Swiper>
    </div>
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, default: () => ({}) },
})
const route = useRoute()
const pageId = inject('pageId', null)
const pageBlueprint = inject('pageBlueprint', null)

const { locale } = useI18n()
const fetchedArticles = ref(null)

const breakpoints = {
  640: {
    // sm
    slidesOffsetBefore: 40,
    slidesOffsetAfter: 40,
    spaceBetween: 20,
  },
}

// FUNCTIONS
async function fetchArticles() {
  let payload = await useAsyncGql({
    operation:
      pageBlueprint === 'sustainability_fact'
        ? 'SustainabilityFacts'
        : 'SustainabilityArticles',
    variables: {
      sort: 'date desc',
      limit: 3,
      filter: {
        site: locale.value,
        id: { not: pageId },
        article_type: switchContainsOrIn(props.data?.restricted_article_types),
      },
    },
  })

  payload = payload.data.value?.entries

  if (!payload) {
    console.error('SustainabilityArticlesTeaser: Articles could not be fetched')
    return
  }

  return payload.data
}

function getRandomInt(max) {
  return Math.floor(Math.random() * max)
}

// COMPUTED
const articles = computed(() => {
  if (props.data.selection.value === 'newest') {
    return fetchedArticles.value || []
  }

  return props.data.nachhaltigkeits_artikel || []
})

const randomInt = getRandomInt(articles.value.length)

const composedArticles = computed(() => {
  // maximal three news
  // a video is col-span-2
  // maximal one article is col-span-2, all others are col-span-1 (even if there are two videos)
  // if there are only three articles, one of them is random col-span-2

  let articlesComposed = []
  let spanCounter = 0
  let maxOneVideoReached = false
  const articlesLength = articles.value.length

  for (let index = 0; index < articlesLength; index++) {
    const article = JSON.parse(JSON.stringify(articles.value[index]))
    const isVideo = !!article.image_or_video[0]?.video

    if (isVideo && !maxOneVideoReached && spanCounter < 3) {
      article.colSpan = 'col-span-2'
      spanCounter += 2
      articlesComposed.push(article)
      maxOneVideoReached = true
      continue
    }

    if (spanCounter < 4) {
      article.colSpan = 'col-span-1'
      spanCounter += 1
      articlesComposed.push(article)
    }

    if (index === articles.value.length - 1 && spanCounter < 4) {
      articlesComposed[randomInt].colSpan = 'col-span-2'
    }
  }

  return articlesComposed
})

// WATCHERS
watch(
  () => props.data,
  async (newValue) => {
    if (newValue.selection?.value === 'newest') {
      fetchedArticles.value = await fetchArticles()
    }
  },
  { deep: true }
)

// INITIAL
if (props.data.selection?.value === 'newest') {
  fetchedArticles.value = await fetchArticles()
}
</script>
